import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Cpu,
  Database,
  Wifi,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Key,
  CreditCard,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { usageApi, UsageSummary, MonthlyCostEntry } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { UsageWidget } from '../components/UsageWidget';
import clsx from 'clsx';

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  unit?: string;
  change?: number;
  limit?: number;
  current?: number;
  costUsd?: number;
}

function StatCard({ icon: Icon, label, value, unit, change, limit, current, costUsd }: StatCardProps) {
  const percentage = limit && limit > 0 && current !== undefined
    ? Math.min((current / limit) * 100, 100)
    : undefined;

  return (
    <div className="card p-5 hover:border-border-secondary transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-accent" />
        </div>
        {change !== undefined && (
          <div
            className={clsx(
              'flex items-center gap-1 text-xs font-medium',
              change >= 0 ? 'text-success' : 'text-error'
            )}
          >
            {change >= 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            {Math.abs(change).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-2xl font-semibold text-foreground tabular-nums">
          {typeof value === 'number' ? value.toLocaleString() : value}
          {unit && <span className="text-sm text-foreground-secondary ml-1">{unit}</span>}
        </p>
        <p className="text-sm text-foreground-tertiary">{label}</p>
      </div>
      {costUsd !== undefined && costUsd > 0 && (
        <div className="mt-2 flex items-center gap-1 text-xs text-foreground-secondary">
          <DollarSign className="w-3 h-3" />
          <span>${costUsd.toFixed(6)} this month</span>
        </div>
      )}
      {percentage !== undefined && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-foreground-tertiary mb-1.5">
            <span>{current?.toLocaleString()} used</span>
            <span>{limit === -1 ? 'Unlimited' : `${limit?.toLocaleString()} limit`}</span>
          </div>
          <div className="usage-meter">
            <div
              className={clsx(
                'usage-meter-fill',
                percentage > 90 ? 'usage-meter-fill-danger' :
                percentage > 75 ? 'usage-meter-fill-warning' :
                'usage-meter-fill-normal'
              )}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function QuickAction({ icon: Icon, label, href }: { icon: React.ElementType; label: string; href: string }) {
  return (
    <Link
      to={href}
      className="flex items-center justify-between p-4 bg-background-tertiary hover:bg-background-hover border border-border rounded-lg transition-colors group"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-foreground-secondary" />
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <ArrowRight className="w-4 h-4 text-foreground-muted group-hover:text-foreground-secondary transition-colors" />
    </Link>
  );
}

function formatBillingPeriod(period: string): string {
  const [year, month] = period.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function CostHistoryTable({ costHistory }: { costHistory: MonthlyCostEntry[] }) {
  const [expanded, setExpanded] = useState(false);
  const displayItems = expanded ? costHistory : costHistory.slice(0, 6);

  if (costHistory.length === 0) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-foreground mb-2">Cost History</h2>
        <p className="text-sm text-foreground-tertiary">No cost history available yet. Costs will appear here as you use the platform.</p>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Monthly Cost History</h2>
          <p className="text-sm text-foreground-tertiary">Recorded costs by billing period for transparency</p>
        </div>
        <Link
          to="/billing"
          className="text-sm text-accent hover:text-accent-hover transition-colors"
        >
          View invoices →
        </Link>
      </div>

      {/* Cost chart for visual overview */}
      {costHistory.length > 1 && (
        <div className="h-40 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={[...costHistory].reverse().slice(-12)}>
              <XAxis
                dataKey="billingPeriod"
                stroke="#52525b"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => {
                  const [, m] = v.split('-');
                  const date = new Date(2000, parseInt(m) - 1);
                  return date.toLocaleDateString('en', { month: 'short' });
                }}
              />
              <YAxis
                stroke="#52525b"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `$${v}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#171717',
                  border: '1px solid #27272a',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ color: '#a1a1aa' }}
                formatter={(value: number) => [`$${value.toFixed(6)}`, 'Total Cost']}
                labelFormatter={(label) => formatBillingPeriod(label)}
              />
              <Bar
                dataKey="totalCostUsd"
                fill="#6366f1"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Detailed table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 text-foreground-tertiary font-medium">Period</th>
              <th className="text-right py-2 px-3 text-foreground-tertiary font-medium hidden sm:table-cell">GPU Compute</th>
              <th className="text-right py-2 px-3 text-foreground-tertiary font-medium hidden sm:table-cell">API Calls</th>
              <th className="text-right py-2 px-3 text-foreground-tertiary font-medium hidden lg:table-cell">Storage</th>
              <th className="text-right py-2 px-3 text-foreground-tertiary font-medium hidden lg:table-cell">Bandwidth</th>
              <th className="text-right py-2 px-3 text-foreground-tertiary font-medium">Total</th>
              <th className="text-center py-2 px-3 text-foreground-tertiary font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((entry) => (
              <tr
                key={entry.billingPeriod}
                className="border-b border-border/50 hover:bg-background-tertiary transition-colors"
              >
                <td className="py-2.5 px-3 text-foreground font-medium">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-foreground-muted" />
                    {formatBillingPeriod(entry.billingPeriod)}
                  </div>
                </td>
                <td className="py-2.5 px-3 text-right text-foreground-secondary tabular-nums hidden sm:table-cell">
                  <div>{entry.gpuComputeHours.toFixed(6)}h</div>
                  <div className="text-xs text-foreground-muted">${entry.gpuComputeCost.toFixed(6)}</div>
                </td>
                <td className="py-2.5 px-3 text-right text-foreground-secondary tabular-nums hidden sm:table-cell">
                  <div>{entry.apiCalls.toLocaleString()}</div>
                  <div className="text-xs text-foreground-muted">${entry.apiCallsCost.toFixed(6)}</div>
                </td>
                <td className="py-2.5 px-3 text-right text-foreground-secondary tabular-nums hidden lg:table-cell">
                  <div>{entry.storageGb.toFixed(6)} GB</div>
                  <div className="text-xs text-foreground-muted">${entry.storageCost.toFixed(6)}</div>
                </td>
                <td className="py-2.5 px-3 text-right text-foreground-secondary tabular-nums hidden lg:table-cell">
                  <div>{entry.bandwidthGb.toFixed(6)} GB</div>
                  <div className="text-xs text-foreground-muted">${entry.bandwidthCost.toFixed(6)}</div>
                </td>
                <td className="py-2.5 px-3 text-right font-semibold text-foreground tabular-nums">
                  ${entry.totalCostUsd.toFixed(6)}
                </td>
                <td className="py-2.5 px-3 text-center">
                  {entry.isFinal ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success">
                      Final
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning/10 text-warning">
                      In progress
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {costHistory.length > 6 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex items-center gap-1 text-sm text-accent hover:text-accent-hover transition-colors mx-auto"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4" /> Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" /> Show all {costHistory.length} months
            </>
          )}
        </button>
      )}
    </div>
  );
}

export function OverviewPage() {
  const { user } = useAuth();
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [usageHistory, setUsageHistory] = useState<Array<{ date: string; GPU_COMPUTE: number; API_CALLS: number; costUsd: number }>>([]);
  const [costHistory, setCostHistory] = useState<MonthlyCostEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summary, history, costs] = await Promise.all([
          usageApi.getSummary(),
          usageApi.getHistory(14),
          usageApi.getCostHistory(),
        ]);
        setUsageSummary(summary);
        setUsageHistory(history.history);
        setCostHistory(costs.costHistory);
      } catch (error) {
        console.error('Failed to fetch usage data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateChange = (current: number, previous: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-40 skeleton rounded-lg" />
          ))}
        </div>
        <div className="h-24 skeleton rounded-lg" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  const summary = usageSummary?.summary;
  const currentMonthTotalCost = usageSummary?.currentMonthTotalCost ?? 0;
  const currentMonthEarnings = usageSummary?.currentMonthEarnings ?? 0;
  const totalEarnings = usageSummary?.totalEarnings ?? 0;
  const pendingEarnings = usageSummary?.pendingEarnings ?? 0;
  const accountBalance = usageSummary?.balance ?? user?.balance ?? 0;
  const billingPeriod = usageSummary?.billingPeriod ?? '';
  const fmt6 = (n: number) => n.toFixed(6);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          Welcome back, {user?.firstName || 'Developer'}
        </h1>
        <p className="text-foreground-secondary mt-1">
          Here's what's happening with your platform usage
        </p>
      </div>

      {/* Balance & Earnings Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-5 border-accent/30 bg-accent/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-accent" />
            </div>
            <p className="text-sm text-foreground-secondary">Total Balance</p>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums">${fmt6(accountBalance)}</p>
          <p className="text-xs text-foreground-muted mt-1">Available account balance</p>
        </div>
        <div className="card p-5 border-error/30 bg-error/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-error" />
            </div>
            <p className="text-sm text-foreground-secondary">
              Current Month Cost
            </p>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums">${fmt6(currentMonthTotalCost)}</p>
          <p className="text-xs text-foreground-muted mt-1">
            {billingPeriod ? formatBillingPeriod(billingPeriod) : '—'}
          </p>
        </div>
        <div className="card p-5 border-success/30 bg-success/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-success" />
            </div>
            <p className="text-sm text-foreground-secondary">
              Current Month Earnings
            </p>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums">${fmt6(currentMonthEarnings)}</p>
          <div className="flex gap-4 mt-1 text-xs text-foreground-muted">
            <span>Total: ${fmt6(totalEarnings)}</span>
            <span>Pending: ${fmt6(pendingEarnings)}</span>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="card p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-foreground-secondary">
                Cost Breakdown ({billingPeriod ? formatBillingPeriod(billingPeriod) : '—'})
              </p>
              <p className="text-xs text-foreground-muted mt-0.5">
                Resets at the start of each billing month
              </p>
            </div>
          </div>
          <div className="hidden sm:flex gap-6 text-sm">
            <div className="text-center">
              <p className="text-foreground-muted">GPU (incl. rentals)</p>
              <p className="font-medium text-foreground tabular-nums">${fmt6(summary?.GPU_COMPUTE?.costUsd ?? 0)}</p>
            </div>
            <div className="text-center">
              <p className="text-foreground-muted">API</p>
              <p className="font-medium text-foreground tabular-nums">${fmt6(summary?.API_CALLS?.costUsd ?? 0)}</p>
            </div>
            <div className="text-center">
              <p className="text-foreground-muted">Storage</p>
              <p className="font-medium text-foreground tabular-nums">${fmt6(summary?.STORAGE?.costUsd ?? 0)}</p>
            </div>
            <div className="text-center">
              <p className="text-foreground-muted">Bandwidth</p>
              <p className="font-medium text-foreground tabular-nums">${fmt6(summary?.BANDWIDTH?.costUsd ?? 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Cpu}
          label="GPU Compute"
          value={summary?.GPU_COMPUTE?.current || 0}
          unit="hours"
          change={summary ? calculateChange(summary.GPU_COMPUTE.current, summary.GPU_COMPUTE.previous) : undefined}
          limit={summary?.GPU_COMPUTE?.limit}
          current={summary?.GPU_COMPUTE?.current}
          costUsd={summary?.GPU_COMPUTE?.costUsd}
        />
        <StatCard
          icon={Activity}
          label="API Calls"
          value={summary?.API_CALLS?.current || 0}
          change={summary ? calculateChange(summary.API_CALLS.current, summary.API_CALLS.previous) : undefined}
          limit={summary?.API_CALLS?.limit}
          current={summary?.API_CALLS?.current}
          costUsd={summary?.API_CALLS?.costUsd}
        />
        <StatCard
          icon={Database}
          label="Storage"
          value={summary?.STORAGE?.current || 0}
          unit="GB"
          change={summary ? calculateChange(summary.STORAGE.current, summary.STORAGE.previous) : undefined}
          limit={summary?.STORAGE?.limit}
          current={summary?.STORAGE?.current}
          costUsd={summary?.STORAGE?.costUsd}
        />
        <StatCard
          icon={Wifi}
          label="Bandwidth"
          value={summary?.BANDWIDTH?.current || 0}
          unit="GB"
          change={summary ? calculateChange(summary.BANDWIDTH.current, summary.BANDWIDTH.previous) : undefined}
          limit={summary?.BANDWIDTH?.limit}
          current={summary?.BANDWIDTH?.current}
          costUsd={summary?.BANDWIDTH?.costUsd}
        />
      </div>

      {/* Usage Widget */}
      <UsageWidget showTitle={true} compact={false} />

      {/* Usage chart */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Usage Trends</h2>
            <p className="text-sm text-foreground-tertiary">Last 14 days</p>
          </div>
          <Link
            to="/usage"
            className="text-sm text-accent hover:text-accent-hover transition-colors"
          >
            View details →
          </Link>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={usageHistory}>
              <defs>
                <linearGradient id="colorGpu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorApi" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                stroke="#52525b"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => new Date(value).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
              />
              <YAxis
                stroke="#52525b"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#171717',
                  border: '1px solid #27272a',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ color: '#a1a1aa' }}
              />
              <Area
                type="monotone"
                dataKey="GPU_COMPUTE"
                name="GPU Compute (hrs)"
                stroke="#6366f1"
                fillOpacity={1}
                fill="url(#colorGpu)"
              />
              <Area
                type="monotone"
                dataKey="API_CALLS"
                name="API Calls"
                stroke="#22c55e"
                fillOpacity={1}
                fill="url(#colorApi)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Cost History */}
      <CostHistoryTable costHistory={costHistory} />

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickAction icon={Key} label="Manage API Keys" href="/api-keys" />
        <QuickAction icon={CreditCard} label="View Billing" href="/billing" />
        <QuickAction icon={Activity} label="Usage Details" href="/usage" />
      </div>

      {/* Account Balance & PAYG Status */}
      <div className="card p-6 border-accent/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="badge-accent">Pay-As-You-Go</span>
              {(user as any)?.paygVerified && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-success/10 text-success font-medium">
                  PAYG Verified
                </span>
              )}
            </div>
            <p className="text-sm text-foreground-secondary mt-2">
              Account balance: <strong>${(user?.balance ?? 0).toFixed(6)}</strong>
              {user?.usageLimitUsd ? ` — Monthly limit: $${user.usageLimitUsd.toFixed(2)}` : ''}
            </p>
            {!(user as any)?.paygVerified && (user?.balance ?? 0) <= 5 && (
              <p className="text-xs text-warning mt-1">
                Low balance. Services will stop when funds run out. Set up PAYG on the Billing page for uninterrupted service.
              </p>
            )}
          </div>
          <Link to="/billing" className="btn-secondary flex-shrink-0">
            Manage Billing
          </Link>
        </div>
      </div>
    </div>
  );
}
